import { BaseService } from "../../Base/BaseService";

export interface IMasterProfile {
  id: number;
  userId: number;
  description: string;
  location: string;
  address: string;
  telegram: string;
  isActive: boolean;
}

export interface IMasterService {
  id: number;
  categoryId: number;
  name: string;
  price: number;
  durationMinutes: number;
  isActive: boolean;
}

export interface IMasterPostDetails {
  id: number;
  masterId: number;
  serviceId: number;
  title: string;
  description: string;
  imageUrl: string;
  isApproved: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IUpdatePostResponse {
  id: number;
  masterId: number;
  serviceId: number;
  title: string;
  description: string;
  imageUrl: string;
  isApproved: boolean;
}

export interface IMasterPost {
  id: number;
  masterId: number;
  serviceId: number;
  title: string;
  description: string;
  imageUrl: string;
  isApproved: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IMyPostsResponse {
  posts: IMasterPost[];
  nextCursor: number | null;
  hasMore: boolean;
}

export interface IMasterRating {
  masterId: number;
  averageRating: number;
  reviewsCount: number;
}

export interface ICreateServiceRequest {
  categoryId: number;
  name: string;
  price: number;
  durationMinutes: number;
  isActive: boolean;
}

export interface ICreateServiceResponse {
  id: number;
  masterId: number;
  categoryId: number;
  name: string;
  price: number;
  durationMinutes: number;
  isActive: boolean;
}

export interface ICreatePostResponse {
  id: number;
  masterId: number;
  serviceId: number;
  title: string;
  description: string;
  imageUrl: string;
  isApproved: boolean;
}

export interface IMasterBooking {
  id: number;
  clientUserId: number;
  clientName: string;
  serviceId: number;
  serviceName: string;
  price: number;
  startsAt: string;
  endsAt: string;
  status: string;
  contactPhone: string;
}

export interface IMasterBookingsResponse {
  date: string;
  bookings: IMasterBooking[];
}

export interface ICancelBookingResponse {
  success: boolean;
}

export interface IConfirmBookingResponse {
  id: number;
  status: string;
}
export interface ICompleteBookingResponse {
  id: number;
  status: string;
}

export interface IMasterBookingTasksResponse {
  pending: IMasterBooking[];
  needComplete: IMasterBooking[];
}

export interface IMasterBookingHistoryResponse {
  history: IMasterBooking[];
}

export interface ICompleteBookingResponse {
  id: number;
  status: string;
}

export class MasterProfileService extends BaseService {
  public static async GetMasterMe(): Promise<IMasterProfile> {
    return this.get(this.BaseApiUrl + "/masters/me");
  }

  public static async GetMyServices(): Promise<{ services: IMasterService[] }> {
    return this.get(this.BaseApiUrl + "/services/my");
  }

  public static async GetMyPosts(limit: number = 10, cursor?: number | null): Promise<IMyPostsResponse> {
    let url = this.BaseApiUrl + `/posts/my?limit=${limit}`;

    if (cursor) {
      url += `&cursor=${cursor}`;
    }

    return this.get(url);
  }

  public static async GetRating(masterId: number): Promise<IMasterRating> {
    return this.get(this.BaseApiUrl + `/masters/${masterId}/rating`);
  }

  public static async CreateService(request: ICreateServiceRequest): Promise<ICreateServiceResponse> {
    return this.post(this.BaseApiUrl + "/services", request);
  }

  public static async DeleteService(id: number): Promise<void> {
    return this.delete(this.BaseApiUrl + `/services/${id}`);
  }

  public static async DeletePost(id: number): Promise<void> {
    return this.delete(this.BaseApiUrl + `/posts/${id}`);
  }

  public static async CreatePost(serviceId: number, title: string, description: string, image: File): Promise<ICreatePostResponse> {
    const formData = new FormData();

    formData.append("serviceId", String(serviceId));
    formData.append("title", title);
    formData.append("description", description);
    formData.append("image", image);

    return this.post(this.BaseApiUrl + "/posts", formData);
  }

  public static async UpdateService(id: number, request: ICreateServiceRequest): Promise<ICreateServiceResponse> {
    return this.put(this.BaseApiUrl + `/services/${id}`, request);
  }

  public static async GetPost(id: number): Promise<IMasterPostDetails> {
    return this.get(this.BaseApiUrl + `/posts/${id}`);
  }

  public static async GetMasterBookings(date: string): Promise<IMasterBookingsResponse> {
    return await BaseService.get<IMasterBookingsResponse>(this.BaseApiUrl + `/master/bookings?date=${date}`);
  }

  public static async GetPendingMasterBookings(): Promise<IMasterBookingsResponse> {
    return await BaseService.get<IMasterBookingsResponse>(this.BaseApiUrl + "/master/bookings/pending");
  }

  public static async ConfirmMasterBooking(id: number): Promise<IConfirmBookingResponse> {
    return await BaseService.post<IConfirmBookingResponse>(this.BaseApiUrl + `/master/bookings/${id}/confirm`, {});
  }

  public static async CancelMasterBooking(id: number): Promise<ICancelBookingResponse> {
    return await BaseService.post<ICancelBookingResponse>(this.BaseApiUrl + `/master/bookings/${id}/cancel`, {});
  }

  public static async UpdatePost(id: number, serviceId: number, title: string, description: string, image: File | null): Promise<IMasterPostDetails> {
    const formData = new FormData();

    formData.append("serviceId", serviceId.toString());
    formData.append("title", title);
    formData.append("description", description);

    if (image) {
      formData.append("image", image);
    }

    return await BaseService.put<IMasterPostDetails>(this.BaseApiUrl + `/posts/${id}`, formData);
  }

  public static async GetMasterBookingTasks(): Promise<IMasterBookingTasksResponse> {
    return await BaseService.get<IMasterBookingTasksResponse>(this.BaseApiUrl + "/master/bookings/tasks");
  }

  public static async GetMasterBookingHistory(): Promise<IMasterBookingHistoryResponse> {
    return await BaseService.get<IMasterBookingHistoryResponse>(this.BaseApiUrl + "/master/bookings/history");
  }

  public static async CompleteMasterBooking(id: number): Promise<ICompleteBookingResponse> {
    return await BaseService.post<ICompleteBookingResponse>(this.BaseApiUrl + `/master/bookings/${id}/complete`, {});
  }
}
